<template>
  <HeroSection
    data-aos="fade-up"
    data-aos-once="true"
    :title="hero.title"
    :description="hero.description"
    :type="'about'"
  />

  <GlobalTeamSection
    data-aos="fade-up"
    data-aos-once="true"
    :title="globalTeam.title"
    :description="globalTeam.description"
    :image-name="globalTeam.imageName"
  />

  <LongWaySection data-aos="fade-up" data-aos-once="true" :title="longWay.title" :items="longWay.items" />

  <MediaSection
    data-aos="fade-up"
    data-aos-once="true"
    :title="media.title"
    :description="media.description"
    :items="media.items"
  />

  <AdvantageOurValuesSection
    data-aos="fade-up"
    data-aos-once="true"
    :title="advantage.title"
    :description="advantage.description"
    :items="advantage.items"
    icon-size="small"
  />

  <TeamSection
    data-aos="fade-up"
    data-aos-once="true"
    :title="team.title"
    :description="team.description"
    :items="team.items"
  />

  <BookConsultationSection
    data-aos="fade-up"
    data-aos-once="true"
    :title="book.title"
    :description="book.description"
    :items="book.items"
  />

  <BlogSection data-aos="fade-up" data-aos-once="true" />
</template>

<script lang="ts" setup>
import { LongWayItemsType } from '~/types/long-way-types'
import { nanoid } from 'nanoid'
import { AdvantageItemsType } from '~/types/advantage-types'
import { BookItemsType } from '~/types/book-types'
import { TeamItemsType } from '~/types/team-types'
import { MediaItemTypes } from '~/types/media-types'

useCustomServerMeta({
  title: 'Credsy - Meet our Global Team',
  description:
    'We have an office in South Florida but we embrace the hybrid work model. Our team of global experts has invested over 10,000 hours in licensing and credentialing.',
})

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [{ name: 'Credsy', item: '/' }, { name: 'About us' }],
  }),
])

const hero: {
  title: string
  description: string
} = {
  title: `Making the healthcare system more efficient and affordable.`,
  description:
    'Our goal is to make licensing and credentialing headache-free. So that ultimately, the healthcare system becomes more efficient. And more affordable for your patients.',
}

const globalTeam: {
  title: string
  description: string
  imageName: string
} = {
  title: `Meet our global team`,
  description:
    'We have an office in South Florida but we embrace the hybrid work model.\n\n' +
    'Our team of global experts has invested over 10,000 hours in licensing and credentialing. So we know how the process works for each state – and we’ve done it thousands of times\n\n' +
    'Credsy is committed to simplifying your licensure process. With over 24 combined years of experience, our agents are well aware of the ins and outs of quick, attainable, and affordable licensing. No matter how many states you need licensing in, we’re able to get the job done.\n\n',
  imageName: 'global-team.png',
}

const longWay: {
  title: string
  items: LongWayItemsType
} = {
  title: 'And we’ve come a long way',
  items: [
    { id: nanoid(), result: '14,576', subtitle: 'Licenses processed' },
    { id: nanoid(), result: '24+', subtitle: 'Years of experience' },
    { id: nanoid(), result: '10,000+', subtitle: 'Hours worked' },
    { id: nanoid(), result: '4.9', subtitle: 'Average review rating' },
  ],
}

const media: {
  title: string
  description: string
  items: MediaItemTypes
} = {
  title: 'In Media',
  description: 'See what the rest of the world has to say about Credsy ',
  items: [
    {
      id: nanoid(),
      mediaLink: 'https://www.linkedin.com/pulse/evolving-landscape-telehealth-licensing-reimbursement-what-you',
      description: 'As telehealth continues to reshape the healthcare industry...',
      logoSrc: '/images/logo-linkedin.svg',
      quote: '“The Evolving Landscape of Telehealth Licensing and Reimbursement”',
    },
    {
      id: nanoid(),
      mediaLink: 'https://www.linkedin.com/pulse/harness-power-ai-healthcare-comprehensive-guide-providers/',
      description: 'We keep on exploring healthcare trends. In Edition 3...',
      logoSrc: '/images/logo-linkedin.svg',
      quote: '“Edition 4: Harness the Power of AI in Healthcare: A Comprehensive Guide”',
    },
    {
      id: nanoid(),
      mediaLink: 'https://www.linkedin.com/pulse/comprehensive-guide-compliance-credentialing-virtua-care-providers/',
      description: 'The rapid expansion of telehealth has transformed healthcare delivery, providing access...',
      logoSrc: '/images/logo-linkedin.svg',
      quote: '“Comprehensive Guide on Compliance and Credentialing for Virtual Care Providers”',
    },
    {
      id: nanoid(),
      mediaLink: 'https://www.linkedin.com/pulse/cms-inpatient-payment-rule-2024-key-takeaways-healthcare-providers/',
      description: 'The CMS Inpatient Payment Rule for 2024 is here, and it’s a game-changer for your business...',
      logoSrc: '/images/logo-linkedin.svg',
      quote: '“CMS Inpatient Payment Rule for 2024: Key Takeaways for Healthcare Providers”',
    },
    {
      id: nanoid(),
      mediaLink: 'https://www.linkedin.com/pulse/edition-5-amazing-potential-gpt-4-transforming-healthcare/',
      description: 'Despite some public skepticism, the possible applications of AI reach...',
      logoSrc: '/images/logo-linkedin.svg',
      quote: '“Edition 5: The Amazing Potential of GPT-4 for Transforming Healthcare”',
    },
    {
      id: nanoid(),
      mediaLink:
        'https://www.linkedin.com/posts/credsy_healthcareprofessionals-dea-healthcareproviders-activity-7059917997749993472-vNIM/',
      description: 'All healthcare professionals registered with the DEA, need to be aware...',
      logoSrc: '/images/logo-linkedin.svg',
      quote: '“How to Renew Your DEA License with the New SUD Training Requirement”',
    },
    {
      id: nanoid(),
      mediaLink:
        'https://www.linkedin.com/posts/credsy_healthcare-insurance-patientcare-activity-7057043923856191488-rxH2',
      description:
        'AI is not just a buzzword, it’s a reality. And it’s changing the way we deliver and receive healthcare...',
      logoSrc: '/images/logo-linkedin.svg',
      quote: '“7 AI Companies That Are Changing the Game in Healthcare”',
    },
    {
      id: nanoid(),
      mediaLink:
        'https://finance.yahoo.com/news/credsy-raises-pre-seed-funding-195610491.html',
      description:
        'The administrative expenses make up nearly a quarter of the entire healthcare costs in the United States, with a ...',
      logoSrc: '/images/yahoo.svg',
      quote: 'Credsy Raises Pre-Seed Funding to Revolutionize Licensing and Credentialing',
    },
    {
      id: nanoid(),
      mediaLink:
        'https://markets.businessinsider.com/news/stocks/credsy-unveils-its-game-changing-primary-source-verification-feature-a-new-dawn-in-healthcare-credentialing-1032600961',
      description:
        'In an era where the healthcare industry is grappling with inefficiencies, delays, and escalating costs ...',
      logoSrc: '/images/mi-logo.svg',
      quote: 'Credsy Unveils Its Game-Changing Primary Source Verification Feature',
    },
    {
      id: nanoid(),
      mediaLink:
        'https://www.benzinga.com/pressreleases/23/09/34192348/credsy-introduces-cme-tracking-feature-to-simplify-license-renewals-and-compliance',
      description:
        'Credsy\'s CME Tracking feature is designed to simplify the process of tracking continuing education...',
      logoSrc: '/images/bz-logo.svg',
      quote: 'Credsy Introduces CME Tracking Feature to Simplify License Renewals and Compliance',
    }
  ],
}

const advantage: {
  title: string
  description: string
  items: AdvantageItemsType
} = {
  title: 'We live\nby our values',
  description:
    'At Credsy, from the initial consultation to the final outcome, we go above and beyond to deliver personalized solutions. As a team, we are committed to a culture of continuous improvement. We foster a learning mindset and stay up-to-date with the latest industry trends, regulations, and best practices, and integrate them into our operations.\n' +
    '\n' +
    'We are driven by a passion for excellence, a dedication to continuous improvement, and a commitment to integrity and transparency. ',
  items: [
    {
      iconName: 'primary-care',
      name: 'Be client-obsessed',
      description: 'Making our clients happy is our #1 focus. Everything else comes secondary.',
    },
    {
      iconName: 'health-systems',
      name: 'Continually improve',
      description: 'Our team is always on the lookout for new ways to improve our process.',
    },
    {
      iconName: 'primary-care',
      name: 'Focus on the outcome',
      description:
        'We keep our eyes on the prize: getting you (or your providers) licensed and credentialed. As quickly as possible.',
    },
    {
      iconName: 'health-systems',
      name: 'Act with integrity',
      description:
        'Transparency is our middle name. So we’ll always keep you informed throughout the process. Every step of the way. \n',
    },
  ],
}

const book: {
  title: string
  description: string
  items: BookItemsType
} = {
  title: `Want us to handle your licensing and credentialing needs?`,
  description: 'Book a free consultation to see how we can help you:',
  items: [
    'Get licensed and credentialed faster',
    'Manage your provider network all in one place',
    'Automate your admin – and scale your provider network',
  ],
}

const team: {
  title: string
  description: string
  items: TeamItemsType
} = {
  title: 'Meet the Team',
  description: 'Check out the faces who make it all happen.',
  items: [
    {
      name: 'Fabiana Ali',
      id: nanoid(),
      img: '/images/fabiana-ali-min.jpg',
    },
    {
      name: 'Trudy Wildman',
      id: nanoid(),
      img: '/images/trudy-wildman-min.jpg',
    },
    {
      name: 'Evelyn Cortes',
      id: nanoid(),
      img: '/images/evelyn-cortes-min.jpg',
    },
    {
      name: 'Gabriela Perez',
      id: nanoid(),
      img: '/images/gabriela-perez-min.jpg',
    },
    {
      name: 'Camron Benjamin',
      id: nanoid(),
      img: '/images/camron-benjamin-min.jpg',
    },
  ],
}
</script>

<style lang="scss"></style>
